// Lightweight client helper to call the server-side OpenAI proxy
// Always use this from the browser instead of calling api.openai.com directly

import { supabase } from './supabase';

/**
 * Streaming variant. Calls /api/openai-stream and yields token chunks via onDelta(deltaText).
 * Returns the full accumulated text once the upstream stream finishes.
 *
 * Supports both Chat Completions (`choices[0].delta.content`) and Responses API
 * (`response.output_text.delta`) event shapes.
 */
export async function openaiStream(path, body, onDelta) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('Authentication required. Please sign in to use this feature.');
  }

  const response = await fetch('/api/openai-stream', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({ path, body }),
  });

  if (!response.ok || !response.body) {
    const errText = await response.text();
    if (response.status === 401) throw new Error('Session expired. Please sign in again.');
    if (response.status === 429) throw new Error('Too many requests. Please wait a moment and try again.');
    throw new Error(errText || `OpenAI proxy error: ${response.status}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';
  let accumulated = '';

  const flushDelta = (deltaText) => {
    if (!deltaText) return;
    accumulated += deltaText;
    if (onDelta) {
      try { onDelta(deltaText, accumulated); } catch (_) {}
    }
  };

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    // SSE messages are separated by blank lines.
    let sepIdx;
    while ((sepIdx = buffer.indexOf('\n\n')) !== -1) {
      const rawEvent = buffer.slice(0, sepIdx);
      buffer = buffer.slice(sepIdx + 2);

      // Each event may have multiple `data: ...` lines.
      const dataLines = rawEvent
        .split('\n')
        .filter((l) => l.startsWith('data:'))
        .map((l) => l.slice(5).trim());
      if (dataLines.length === 0) continue;
      const dataPayload = dataLines.join('\n');
      if (dataPayload === '[DONE]') continue;

      try {
        const json = JSON.parse(dataPayload);
        // Chat Completions streaming shape:
        const cc = json?.choices?.[0]?.delta?.content;
        if (typeof cc === 'string' && cc.length) {
          flushDelta(cc);
          continue;
        }
        // Responses API streaming shape — text deltas:
        if (json.type === 'response.output_text.delta' && typeof json.delta === 'string') {
          flushDelta(json.delta);
          continue;
        }
        // Responses API completed event with full output_text:
        if (json.type === 'response.completed' && Array.isArray(json.response?.output)) {
          // No-op; we already streamed the deltas. Could verify here.
          continue;
        }
      } catch (_) {
        // Ignore non-JSON keep-alives or partial frames.
      }
    }
  }

  return accumulated;
}

export async function openai(path, body) {
  // Get the current session token for authentication
  const { data: { session } } = await supabase.auth.getSession();
  
  if (!session?.access_token) {
    throw new Error('Authentication required. Please sign in to use this feature.');
  }

  const response = await fetch('/api/openai', {
    method: 'POST',
    headers: { 
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`
    },
    body: JSON.stringify({ path, body })
  });

  const text = await response.text();
  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Session expired. Please sign in again.');
    }
    if (response.status === 429) {
      throw new Error('Too many requests. Please wait a moment and try again.');
    }
    throw new Error(text || `OpenAI proxy error: ${response.status}`);
  }

  try {
    return JSON.parse(text);
  } catch {
    return text; // some endpoints may legitimately return text
  }
}


